import { PORTABLE_KINDS } from "./document.js";
import { PROFILE_IDS } from "./spec.js";
import { COMPARISON_OPS, GROUP_OPS, OPERAND_KINDS, PRICE_FIELDS } from "./spec.js";
import { indicatorExtensionProblems, offsetProblem } from "./extensions.js";
import { isDataProvider, isMarketType } from "./market.js";
import { STRAT_FORMAT, STRAT_FORMAT_VERSION, STRAT_READABLE_SUFFIXES } from "./version.js";
import { runDataSource } from "./write.js";
/**
 * Collects every problem with a path rather than throwing on the first one.
 *
 * A file with four mistakes should report four mistakes. Fixing a hand-edited
 * config one error per attempt is the difference between a usable format and a
 * guessing game.
 */
class Problems {
    errors;
    prefix;
    /** Every scope shares one array, so a nested check reports into the top-level list. */
    constructor(errors = [], prefix = "") {
        this.errors = errors;
        this.prefix = prefix;
    }
    at(segment) {
        return new Problems(this.errors, this.prefix ? `${this.prefix}.${segment}` : segment);
    }
    add(field, message) {
        const path = [this.prefix, field].filter(Boolean).join(".");
        this.errors.push(path ? `${path}: ${message}` : message);
    }
}
function isObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isNonEmptyString(value) {
    return typeof value === "string" && value.trim() !== "";
}
function checkOperand(value, field, p, indicatorIds) {
    if (!isObject(value))
        return p.add(field, "must be an object");
    const kind = value.kind;
    if (typeof kind !== "string" || !OPERAND_KINDS.includes(kind)) {
        return p.add(`${field}.kind`, `must be one of ${OPERAND_KINDS.join(", ")}`);
    }
    const offset = offsetProblem(value);
    if (offset)
        p.add(`${field}.offset`, offset);
    if (kind === "indicator") {
        if (!isNonEmptyString(value.ref))
            return p.add(`${field}.ref`, "must be a non-empty string");
        // The most common hand-edit mistake by far: renaming an indicator's id and
        // leaving a condition pointing at the old one. The engine would read a
        // series that is null forever and simply never signal, which looks like a
        // bad strategy rather than a broken file.
        if (!indicatorIds.has(value.ref)) {
            p.add(`${field}.ref`, `"${value.ref}" does not match any indicator id on this strategy`);
        }
    }
    else if (kind === "price") {
        if (typeof value.field !== "string" || !PRICE_FIELDS.includes(value.field)) {
            p.add(`${field}.field`, `must be one of ${PRICE_FIELDS.join(", ")}`);
        }
    }
    else if (!Number.isFinite(value.value)) {
        // Note `Number.isFinite`, not `!Number.isNaN`: an Infinity operand compares
        // as a real number and would fire conditions.
        p.add(`${field}.value`, "must be a finite number");
    }
}
/** Recursive: a malformed node five levels down is still a malformed file. */
function checkCondition(value, field, p, indicatorIds, depth = 0) {
    if (depth > 20)
        return p.add(field, "nested too deeply");
    if (!isObject(value))
        return p.add(field, "must be an object");
    const op = value.op;
    if (typeof op !== "string")
        return p.add(`${field}.op`, "is required");
    if (GROUP_OPS.includes(op)) {
        if (!Array.isArray(value.children))
            return p.add(`${field}.children`, "must be an array");
        value.children.forEach((child, i) => checkCondition(child, `${field}.children[${i}]`, p, indicatorIds, depth + 1));
        return;
    }
    if (!COMPARISON_OPS.includes(op)) {
        return p.add(`${field}.op`, `unknown operator "${op}" — expected one of ${[...GROUP_OPS, ...COMPARISON_OPS].join(", ")}`);
    }
    checkOperand(value.left, `${field}.left`, p, indicatorIds);
    checkOperand(value.right, `${field}.right`, p, indicatorIds);
}
function checkStrategySpec(value, p) {
    if (!isObject(value))
        return p.add("", "must be an object");
    if (!isNonEmptyString(value.name))
        p.add("name", "is required");
    if (value.direction !== undefined && value.direction !== "long" && value.direction !== "short") {
        p.add("direction", 'must be "long" or "short" when present');
    }
    if (value.risk !== undefined && !isObject(value.risk))
        p.add("risk", "must be an object when present");
    const indicatorIds = new Set();
    if (!Array.isArray(value.indicators)) {
        p.add("indicators", "must be an array");
    }
    else {
        value.indicators.forEach((def, i) => {
            if (!isObject(def))
                return p.add(`indicators[${i}]`, "must be an object");
            if (!isNonEmptyString(def.id))
                p.add(`indicators[${i}].id`, "is required");
            else if (indicatorIds.has(def.id))
                p.add(`indicators[${i}].id`, `duplicate id "${def.id}"`);
            else
                indicatorIds.add(def.id);
            if (!isNonEmptyString(def.kind))
                p.add(`indicators[${i}].kind`, "is required");
            if (def.params !== undefined && !isObject(def.params)) {
                p.add(`indicators[${i}].params`, "must be an object");
            }
        });
        for (const problem of indicatorExtensionProblems(value.indicators))
            p.add(problem.field, problem.message);
    }
    if (value.entry === undefined)
        p.add("entry", "is required");
    else
        checkCondition(value.entry, "entry", p, indicatorIds);
    if (value.exit === undefined)
        p.add("exit", "is required");
    else
        checkCondition(value.exit, "exit", p, indicatorIds);
}
function checkSettings(value, p) {
    if (!isObject(value))
        return p.add("", "must be an object when present");
    if (!isNonEmptyString(value.symbol))
        p.add("symbol", "is required");
    if (!isNonEmptyString(value.timeframe))
        p.add("timeframe", "is required");
    if (typeof value.initialCapital !== "number" || !(value.initialCapital > 0)) {
        p.add("initialCapital", "must be a positive number");
    }
    for (const field of ["from", "to"]) {
        const bound = value[field];
        if (bound !== undefined && bound !== null && !Number.isFinite(bound)) {
            p.add(field, "must be epoch milliseconds or null");
        }
    }
}
/**
 * Results are checked at the surface only.
 *
 * A two-year 5m run carries ~210k equity points, and walking every one to
 * confirm it has two numeric fields costs more than it catches — a truncated or
 * foreign file fails on the shape long before it fails on point 154,000. What
 * is checked is what a reader touches without guarding first.
 */
function checkResult(value, p) {
    if (!isObject(value))
        return p.add("", "must be an object");
    for (const field of ["trades", "orders", "equityCurve"]) {
        if (!Array.isArray(value[field]))
            p.add(field, "must be an array");
    }
    if (!isObject(value.stats))
        p.add("stats", "must be an object");
    else if (typeof value.stats.totalReturnPct !== "number") {
        p.add("stats.totalReturnPct", "must be a number");
    }
}
function checkRun(value, p) {
    if (!isObject(value))
        return p.add("", "must be an object");
    if (!isNonEmptyString(value.symbol))
        p.add("symbol", "is required");
    if (!isNonEmptyString(value.timeframe))
        p.add("timeframe", "is required");
    if (!isNonEmptyString(value.ranAt))
        p.add("ranAt", "is required — an ISO-8601 timestamp");
    if (!isObject(value.params))
        p.add("params", "must be an object");
    if (value.strategy === undefined)
        p.add("strategy", "is required");
    else
        checkStrategySpec(value.strategy, p.at("strategy"));
    if (value.result === undefined)
        p.add("result", "is required");
    else
        checkResult(value.result, p.at("result"));
    if (value.dataSource !== undefined)
        checkDataSource(value.dataSource, p.at("dataSource"));
}
/**
 * The provider is deliberately *not* checked against the list this build
 * knows: a file from a newer build may name one added since, and refusing it
 * would make a new provider a breaking change. It is noted instead.
 */
function checkDataSource(value, p) {
    if (!isObject(value))
        return p.add("", "must be an object when present");
    if (!isNonEmptyString(value.provider))
        p.add("provider", "is required");
    if (!isNonEmptyString(value.symbol))
        p.add("symbol", "is required");
    if (!isNonEmptyString(value.timeframe))
        p.add("timeframe", "is required");
    if (!isMarketType(value.marketType))
        p.add("marketType", "must be a known market type");
    for (const field of ["from", "to"]) {
        const bound = value[field];
        if (bound !== null && !Number.isFinite(bound))
            p.add(field, "must be epoch milliseconds or null");
    }
    for (const field of ["warmupBars", "barsTested"]) {
        const n = value[field];
        if (typeof n !== "number" || !(n >= 0))
            p.add(field, "must be a number, 0 or more");
    }
}
function checkProfile(value, p) {
    if (value === undefined)
        return undefined;
    if (typeof value !== "string" || !PROFILE_IDS.includes(value)) {
        p.add("profile", `must be one of ${PROFILE_IDS.join(", ")} when present`);
        return undefined;
    }
    return value;
}
/**
 * Reads any `.strat` file into one shape.
 *
 * Returns every problem at once rather than the first, and never throws: a
 * caller's job is to show `errors`, not to catch.
 */
export function readPortable(value) {
    if (!isObject(value)) {
        return { ok: false, errors: ["Expected a JSON object at the top level."] };
    }
    if (value.format !== STRAT_FORMAT) {
        return {
            ok: false,
            errors: [
                `Not a .strat file — expected "format": "${STRAT_FORMAT}"${typeof value.format === "string" ? `, found "${value.format}"` : ""}.`,
            ],
        };
    }
    if (typeof value.formatVersion !== "number") {
        return { ok: false, errors: ['"formatVersion" is required and must be a number.'] };
    }
    if (value.formatVersion > STRAT_FORMAT_VERSION) {
        return {
            ok: false,
            errors: [
                `This file is format version ${value.formatVersion}; this build reads up to ${STRAT_FORMAT_VERSION}. Update to open it.`,
            ],
        };
    }
    if (typeof value.kind !== "string" || !PORTABLE_KINDS.includes(value.kind)) {
        return { ok: false, errors: [`"kind" must be one of ${PORTABLE_KINDS.join(", ")}.`] };
    }
    const notes = [];
    const p = new Problems();
    const strategies = [];
    const runs = [];
    const kind = value.kind;
    if (kind === "strategy") {
        checkStrategySpec(value.strategy, p.at("strategy"));
        if (value.settings !== undefined)
            checkSettings(value.settings, p.at("settings"));
        const profile = checkProfile(value.profile, p);
        if (p.errors.length === 0) {
            strategies.push({
                strategy: value.strategy,
                ...(value.settings ? { settings: value.settings } : {}),
                ...(profile ? { profile } : {}),
            });
        }
    }
    else if (kind === "run") {
        if (!isObject(value.run))
            p.add("run", "is required");
        else {
            checkRun(value.run, p.at("run"));
            if (p.errors.length === 0)
                runs.push(normalizeRun(value.run, notes));
        }
    }
    else {
        const rawStrategies = value.strategies ?? [];
        const rawRuns = value.runs ?? [];
        if (!Array.isArray(rawStrategies))
            p.add("strategies", "must be an array");
        if (!Array.isArray(rawRuns))
            p.add("runs", "must be an array");
        if (Array.isArray(rawStrategies)) {
            rawStrategies.forEach((entry, i) => {
                const scope = p.at(`strategies[${i}]`);
                if (!isObject(entry))
                    return scope.add("", "must be an object");
                checkStrategySpec(entry.strategy, scope.at("strategy"));
                if (entry.settings !== undefined)
                    checkSettings(entry.settings, scope.at("settings"));
                checkProfile(entry.profile, scope);
                strategies.push(entry);
            });
        }
        if (Array.isArray(rawRuns)) {
            rawRuns.forEach((entry, i) => {
                const scope = p.at(`runs[${i}]`);
                checkRun(entry, scope);
                if (isObject(entry))
                    runs.push(normalizeRun(entry, notes));
            });
        }
        if (strategies.length === 0 && runs.length === 0 && p.errors.length === 0) {
            p.add("", "This bundle is empty — it contains no strategies and no runs.");
        }
    }
    if (p.errors.length > 0)
        return { ok: false, errors: p.errors };
    const contents = {
        kind,
        strategies,
        runs,
        exportedAt: typeof value.exportedAt === "string" ? value.exportedAt : null,
        generator: isObject(value.generator)
            ? {
                name: String(value.generator.name ?? "unknown"),
                version: String(value.generator.version ?? "unknown"),
            }
            : null,
        notes,
    };
    return { ok: true, contents };
}
/**
 * Fills the provenance fields a hand-written file may not carry.
 *
 * Note what is *not* defaulted: `ranAt`. A run's timestamp is a recorded fact,
 * and substituting the current clock for a missing one would invent history —
 * in a product whose thesis is unedited records, that habit must not exist
 * anywhere, least of all in the reader. A file without it is rejected above.
 */
function normalizeRun(raw, notes) {
    const run = raw;
    if (!run.sourceId)
        notes.push(`Run "${run.strategyName ?? run.symbol}" carried no source id.`);
    const dataSource = runDataSource(run);
    if (!isDataProvider(dataSource.provider)) {
        notes.push(`Run "${run.strategyName ?? run.symbol}" names data provider "${dataSource.provider}", which this build does not know — its bars are looked up by symbol and market only.`);
    }
    return {
        sourceId: typeof run.sourceId === "string" ? run.sourceId : "",
        strategyName: isNonEmptyString(run.strategyName) ? run.strategyName : "Imported strategy",
        symbol: run.symbol,
        timeframe: run.timeframe,
        marketType: run.marketType,
        leverage: Number.isFinite(run.leverage) ? Math.max(1, run.leverage) : 1,
        batchLabel: typeof run.batchLabel === "string" ? run.batchLabel : null,
        params: run.params,
        ranAt: run.ranAt,
        strategy: run.strategy,
        result: run.result,
        dataSource,
    };
}
/** Whether a filename looks like something this reader will open. */
export function looksPortable(filename) {
    const lower = filename.toLowerCase();
    return STRAT_READABLE_SUFFIXES.some((suffix) => lower.endsWith(suffix));
}
//# sourceMappingURL=read.js.map